import {
  queryOptions,
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { getAuthHeaders } from '@/identity.js';

/**
 * Client for a workspace's knowledge sources (cti-workspace-service, reached via the gateway at
 * `/v1/workspaces/:workspaceId/knowledge/sources`). A source points the workspace's private search
 * index at a SharePoint folder or file; creating or enabling one kicks an east-west ingest in
 * cti-sync-service. Reads are open to any member; mutations are editor+ (owner/editor) — the UI hides
 * them for viewers, the service enforces them (viewer → 403).
 *
 * REST is snake_case on the wire; this maps it to the camelCase the SPA uses. When a field crosses this
 * boundary the exact snake_case key must match the service DTO — `ValidationPipe({ whitelist: true })`
 * silently drops any key it doesn't declare, so a camelCase typo is silent data loss neither TS nor the
 * runtime catches. `type` and `sync_status` are OPEN unions: a newer service can send a value outside
 * the known set, so consumers fall back to a safe default rather than index a lookup map blindly.
 */

const API_BASE = '/v1/workspaces';

/**
 * Known knowledge-source kinds. `files` is a file uploaded from the user's machine, which needs no
 * connector at all; `artifact` is one of the workspace's own working files put on the knowledge shelf —
 * no bytes are copied, the row only points at the file; the two `onedrive_*` kinds are an item of a
 * member's personal OneDrive, registered through that member's own connected folder. Treat as OPEN — a
 * newer service can send an unknown value.
 */
export type KnowledgeSourceType =
  | 'sharepoint_folder'
  | 'sharepoint_file'
  | 'sharepoint_site'
  | 'sharepoint_library'
  | 'onedrive_folder'
  | 'onedrive_file'
  | 'files'
  | 'artifact';

/**
 * Ingest state derived best-effort by the service: `disabled` (source off), `registered` (a sync
 * source exists), `pending` (enabled but not yet registered downstream). Treat as OPEN.
 */
export type KnowledgeSyncStatus = 'disabled' | 'registered' | 'pending';

/** The SharePoint coordinates a source points at. `url` is the item's web URL; `name` is Graph's display name. */
export interface SharePointSourceConfig {
  site_id: string;
  /** Absent only for `sharepoint_site`, which holds several libraries and names no single one. */
  drive_id?: string;
  /**
   * What the source anchors on: a Graph item for a folder or a file, the drive id for a whole library,
   * the site id for a whole site. The upper two levels repeat an id they already carry because neither
   * has a drive item to name — a library's walk starts at its drive root — and `item_id` is half the
   * key the indexer addresses every source by.
   */
  item_id: string;
  /**
   * Which of the workspace's SharePoint connections the item lives in. Optional because every source
   * registered before connections had ids has none, and those keep working — the indexer falls back to
   * the workspace's shared connection, which is where they came from. Where it is present it is part of
   * the address: the three Graph ids are unique inside a tenant, not between tenants. The service
   * accepts it on create and refuses to change or clear it afterwards.
   */
  connection_id?: string;
  url?: string;
  /** Graph `driveItem.name` — preferred for display over the URL basename (`Doc.aspx`). */
  name?: string;
}

/**
 * The item of somebody's personal OneDrive a `onedrive_folder` / `onedrive_file` source points at.
 *
 * The address is the scope plus the item, and the scope is what makes it one: a `scope_id` names the
 * folder its owner granted this workspace, so the pair `scope_id` + `item_id` is what "this one is in the
 * knowledge base already" is matched on. `connection_id` and `drive_id` come from the verified scope,
 * never from the browser — the client sends neither on create.
 *
 * `name` is Graph's display name and `url` the item's web URL. The name is the only readable label there
 * is here: a personal drive's `webUrl` has no meaningful last segment, so nothing derives a name from it.
 */
export interface OneDriveSourceConfig {
  connection_id?: string;
  scope_id: string;
  drive_id: string;
  item_id: string;
  name?: string;
  url?: string;
}

/**
 * The uploaded file a `files` source points at: where its bytes sit in the knowledge bucket plus what it
 * takes to list and serve them. `s3_key` comes from the presign — the client never invents it.
 */
export interface KnowledgeFileConfig {
  s3_key: string;
  filename: string;
  size: number;
  content_type: string;
}

/**
 * The workspace file an `artifact` source points at. The whole locator is the artifact id — never an
 * object key: committing a revision moves the key of the file's head, so a source declared by key would
 * turn into a second source at the first save. It is also the key the indexing contour already registered
 * the file under, which is what lets a shelved file report real indexing state with no second ingest.
 */
export interface ArtifactSourceConfig {
  artifact_id: string;
}

/**
 * A listed source's config, kept as the snake_case wire object because it goes straight back to the
 * service on update. Every field is optional: the shape follows the source kind — SharePoint carries
 * `site_id`/`drive_id`/`item_id`, OneDrive carries `scope_id`/`drive_id`/`item_id`, an upload carries
 * `s3_key`/`filename`/`size`/`content_type`, a shelved workspace file carries `artifact_id` — and `type` is an OPEN union, so a newer service can list a kind
 * whose shape this build has never seen. Reading each field as optional is what keeps that from breaking
 * the list.
 */
export type KnowledgeSourceConfig = Partial<
  SharePointSourceConfig &
    OneDriveSourceConfig &
    KnowledgeFileConfig &
    ArtifactSourceConfig
>;

/**
 * How far the current indexing pass has got, as sync reports it: `queued` (a job is on sync's queue and
 * no document row has moved yet), `running` (a pass is working, or documents are still in flight),
 * `settled` (the pass is over). Treat as OPEN — a newer sync can report a state outside this set.
 *
 * This is the state the client used to guess from counter arithmetic, which could not see the queue at
 * all: a reindex of a settled source answered `queued` while the counts still read complete, so the
 * badge froze on pre-reindex numbers and the poll stopped before any work started.
 */
export type KnowledgeIndexingPass = 'queued' | 'running' | 'settled';

/**
 * Real indexing progress for a source, read by the service from cti-sync-service. This is what tells a
 * user whether the content is actually searchable — `syncStatus` only says a sync source exists.
 * `needsReconnect` means ingest is stalled until SharePoint is reconnected; `lastSyncedAt` is null before
 * the first successful pass. `isActive` is the INDEXER's own on/off flag, which is not the same as the
 * shell's `enabled`: the shell's pause reaches the indexer best-effort, so the two can disagree.
 */
export interface KnowledgeSourceIndexing {
  /** Pass state as sync reports it — a raw string, since {@link KnowledgeIndexingPass} is an open union. */
  pass: string;
  /**
   * How many documents the source holds, or null when the service did not report it. Nullable because
   * zero already means "this source is empty", which is what the badge says — so a missing field must
   * not borrow that meaning.
   */
  documentsTotal: number | null;
  documentsIndexed: number;
  documentsFailed: number;
  /**
   * How many of the failures will never succeed on a retry (a subset of `documentsFailed`). It is the
   * difference between "reindex may help" and "reindex will not", which is the only reason the row
   * mentions it.
   */
  documentsFailedPermanent: number;
  lastSyncedAt: string | null;
  needsReconnect: boolean;
  isActive: boolean;
  /**
   * Display name sync stamped from Graph after ingest. Used when the shell config has no `name` /
   * `filename` — classic SharePoint `webUrl`s often end in `Doc.aspx`.
   */
  name: string | null;
  /**
   * How many files the last folder pass left out because the source had no room left for them: the
   * folder holds more content than one source indexes. Zero when nothing was left out, and for a service
   * too old to report it.
   */
  filesOverBudget: number;
}

/**
 * A knowledge source as the SPA consumes it (camelCase). `type`/`syncStatus` are raw strings (open
 * union) so an unknown value never breaks parsing — the UI resolves a label/fallback. `indexing` is null
 * when the service could not read the status (sync unreachable) — consumers fall back to `syncStatus`.
 * `config` is kept as the snake_case wire object because it is passed straight back to the service on
 * update and to the agent's search branch; re-casing it would risk a boundary mismatch.
 */
export interface KnowledgeSource {
  id: string;
  workspaceId: string;
  type: string;
  config: KnowledgeSourceConfig;
  enabled: boolean;
  syncStatus: string;
  /**
   * Nobody's access is on record for this source: it is a SharePoint source that names no connection at
   * all. It is NOT the same state as `indexing.needsReconnect`, which is a source naming a connection
   * that no longer resolves, and the two must not be collapsed — an unclaimed source is still indexed and
   * still searchable, while a source whose connection broke has stopped being refreshed. The repair is the
   * same action in both cases, and the sentence a person reads is not.
   */
  needsConnection: boolean;
  indexing: KnowledgeSourceIndexing | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * The snake_case indexing block. Every field is optional: an older or degraded service can omit any of
 * them, and a partial block must not break the mapping — nor invent a state (a missing `is_active` is no
 * evidence of a pause, so it maps to true).
 */
interface KnowledgeSourceIndexingWire {
  pass?: string;
  documents_total?: number;
  documents_indexed?: number;
  documents_failed?: number;
  documents_failed_permanent?: number;
  last_synced_at?: string | null;
  needs_reconnect?: boolean;
  is_active?: boolean;
  name?: string | null;
  files_over_budget?: number;
}

/** The snake_case shape the service returns per source. */
interface KnowledgeSourceWire {
  id: string;
  workspace_id: string;
  type: string;
  config: KnowledgeSourceConfig;
  enabled: boolean;
  sync_status?: string;
  needs_connection?: boolean;
  indexing?: KnowledgeSourceIndexingWire | null;
  created_at: string;
  updated_at: string;
}

/**
 * Whether refreshing has stopped on either side. `enabled` is the workspace shell's flag, `isActive` the
 * indexer's own, and the shell's pause reaches the indexer best-effort — so they can disagree, and either
 * one off means the content is no longer being refreshed. Lives here rather than in the UI because the
 * polling predicate below needs the same answer the badge does.
 */
export function isKnowledgeSourcePaused(source: KnowledgeSource): boolean {
  return !source.enabled || source.indexing?.isActive === false;
}

/**
 * Body for creating a SharePoint source. `config` and `type` are already the snake_case wire shape.
 *
 * All four levels are creatable, because anything the allow-list can name, the knowledge base can
 * hold. The config differs only in which ids it carries — see {@link SharePointSourceConfig}.
 */
export interface CreateSharePointSourceInput {
  type:
    | 'sharepoint_folder'
    | 'sharepoint_file'
    | 'sharepoint_site'
    | 'sharepoint_library';
  config: SharePointSourceConfig;
  enabled: boolean;
}

/**
 * Body for registering an already-uploaded file as a source. Sent only after its bytes are in the bucket:
 * the service reads them to index, so registering first would give sync nothing to fetch.
 */
export interface CreateFileSourceInput {
  type: 'files';
  config: KnowledgeFileConfig;
  enabled: boolean;
}

/**
 * Body for putting one of the workspace's own files on the knowledge shelf. No `enabled`: a shelved file
 * has no pause (the indexer's document list excludes it, so the switch would be a prop), and the service
 * defaults the flag to true.
 */
export interface CreateArtifactSourceInput {
  type: 'artifact';
  config: ArtifactSourceConfig;
}

/** Any kind of create body. The `type` discriminates which `config` shape the service expects. */
export type CreateKnowledgeSourceInput =
  | CreateSharePointSourceInput
  | CreateFileSourceInput
  | CreateArtifactSourceInput;

/**
 * Map the indexing block, defaulting anything the service left out so a partial block still renders —
 * except `documents_total`, which stays null when absent. The counters can default to zero safely: with a
 * known total, "0 of 42 indexed" reads as work still to do and self-corrects on the next poll, whereas a
 * zero *total* would assert the source is empty.
 *
 * A missing `pass` reads as `settled`. A service too old to report it cannot be polled into telling the
 * truth, so the alternative — assuming work — would keep the list asking sync every 15 s for an answer
 * that can never arrive.
 */
function indexingFromWire(
  wire: KnowledgeSourceIndexingWire | null | undefined,
): KnowledgeSourceIndexing | null {
  if (!wire) return null;
  return {
    pass:
      typeof wire.pass === 'string' && wire.pass.trim().length > 0
        ? wire.pass
        : 'settled',
    documentsTotal:
      typeof wire.documents_total === 'number' ? wire.documents_total : null,
    documentsIndexed:
      typeof wire.documents_indexed === 'number' ? wire.documents_indexed : 0,
    documentsFailed:
      typeof wire.documents_failed === 'number' ? wire.documents_failed : 0,
    documentsFailedPermanent:
      typeof wire.documents_failed_permanent === 'number'
        ? wire.documents_failed_permanent
        : 0,
    lastSyncedAt: wire.last_synced_at ?? null,
    needsReconnect: wire.needs_reconnect === true,
    isActive: wire.is_active !== false,
    name:
      typeof wire.name === 'string' && wire.name.length > 0 ? wire.name : null,
    filesOverBudget:
      typeof wire.files_over_budget === 'number' ? wire.files_over_budget : 0,
  };
}

function fromWire(wire: KnowledgeSourceWire): KnowledgeSource {
  return {
    id: wire.id,
    workspaceId: wire.workspace_id,
    type: wire.type,
    config: wire.config,
    enabled: wire.enabled,
    syncStatus: wire.sync_status ?? (wire.enabled ? 'pending' : 'disabled'),
    needsConnection: wire.needs_connection === true,
    indexing: indexingFromWire(wire.indexing),
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
  };
}

async function headers(): Promise<Record<string, string>> {
  return { 'Content-Type': 'application/json', ...(await getAuthHeaders()) };
}

async function errorMessage(res: Response, fallback: string): Promise<string> {
  const text = await res.text().catch(() => '');
  if (!text.trim()) return `${fallback} (${res.status})`;
  try {
    const body = JSON.parse(text) as { message?: string | string[] };
    if (Array.isArray(body.message)) return body.message.join('; ');
    if (typeof body.message === 'string' && body.message.trim())
      return body.message;
  } catch {
    /* not JSON */
  }
  return text.trim();
}

function sourcesPath(workspaceId: string): string {
  return `${API_BASE}/${encodeURIComponent(workspaceId)}/knowledge/sources`;
}

/** List a workspace's knowledge sources. Response is wrapped — `{ sources: [...] }`. */
export async function listKnowledgeSources(
  workspaceId: string,
): Promise<KnowledgeSource[]> {
  const res = await fetch(sourcesPath(workspaceId), {
    headers: await headers(),
  });
  if (!res.ok)
    throw new Error(
      await errorMessage(res, 'Failed to load knowledge sources'),
    );
  const body = (await res.json()) as { sources?: KnowledgeSourceWire[] };
  return (body.sources ?? []).map(fromWire);
}

/**
 * Create a knowledge source (editor+). The two kinds fail for different reasons, so they get different
 * words: on a SharePoint source a 409 means the same item is already registered under another workspace
 * (the global `folders.external_id` is unique; cross-workspace duplication is deferred), while on a file
 * it means the workspace is already at its upload cap. Both beat the raw conflict body.
 */
export async function createKnowledgeSource(
  workspaceId: string,
  input: CreateKnowledgeSourceInput,
): Promise<KnowledgeSource> {
  const res = await fetch(sourcesPath(workspaceId), {
    method: 'POST',
    headers: await headers(),
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    if (input.type === 'files') {
      throw new Error(
        uploadRefusalMessage(res.status, input.config.filename) ??
          (await errorMessage(res, 'Failed to add the file')),
      );
    }
    if (input.type === 'artifact') {
      throw new KnowledgePromotionRefusal(
        res.status,
        promotionRefusalMessage(res.status) ??
          (await errorMessage(
            res,
            'Failed to add the file to the knowledge base',
          )),
      );
    }
    if (res.status === 409) {
      throw new KnowledgeSourceAlreadyAdded(
        await errorMessage(
          res,
          'This item is already a knowledge source in this workspace.',
        ),
      );
    }
    throw new Error(await errorMessage(res, 'Failed to add knowledge source'));
  }
  return fromWire((await res.json()) as KnowledgeSourceWire);
}

/**
 * Put one of the workspace's own files on the knowledge shelf. It copies nothing: the row points at the
 * file by id, so the file keeps its single source in the indexing contour and its content stays where it
 * was. In a shared workspace the service also opens the file to every member in the same operation — that
 * is the consequence the confirmation dialog has to name before this is called.
 */
export async function addArtifactToKnowledge(
  workspaceId: string,
  artifactId: string,
): Promise<KnowledgeSource> {
  return createKnowledgeSource(workspaceId, {
    type: 'artifact',
    config: { artifact_id: artifactId },
  });
}

/**
 * Why the service refused a promotion, in words the user can act on — undefined when the status has
 * nothing specific to say. 409 is a file that is already on the shelf (the promotion is idempotent by
 * locator, so a second one is refused rather than duplicated) — a state, not a failure. 403 is the share
 * right, which the UI hides the action on, so it is the race where a role changed under the caller; 404 is
 * a file deleted or never visible to them, which is the state a card left open long enough will hit.
 */
/**
 * A refused promotion, carrying the status so a caller can tell a state from a failure. The message is
 * still the whole story for anything that only shows it — this exists because 409 means "already on the
 * shelf", which is where the user wanted to be, and reporting that in red on a dialog they cannot leave
 * makes a no-op look broken.
 */
/**
 * A create refused because this workspace already holds the item. It is the one 409 a SharePoint
 * create can get, and it is typed because one caller reads it as success: adding a place whose
 * address is already on the knowledge shelf leaves exactly the state that was asked for.
 */
export class KnowledgeSourceAlreadyAdded extends Error {
  readonly status = 409;

  constructor(message: string) {
    super(message);
    this.name = 'KnowledgeSourceAlreadyAdded';
  }
}

export class KnowledgePromotionRefusal extends Error {
  constructor(
    readonly status: number,
    message: string,
  ) {
    super(message);
    this.name = 'KnowledgePromotionRefusal';
  }
}

/** Whether this rejection is the shelf saying the file is already on it. */
export function isAlreadyShelved(reason: unknown): boolean {
  return reason instanceof KnowledgePromotionRefusal && reason.status === 409;
}

function promotionRefusalMessage(status: number): string | undefined {
  if (status === 409) {
    return "This file is already in this workspace's knowledge base.";
  }
  if (status === 403) {
    return "Only the file's author or a workspace owner can add it to the knowledge base.";
  }
  if (status === 404) {
    return 'This file is no longer in this workspace.';
  }
  if (status === 503) {
    return 'The file could not be added — the sync service is unavailable, try again.';
  }
  return undefined;
}

/** Largest file the service will presign — the same 25 MiB cap the chat composer applies to attachments. */
export const MAX_KNOWLEDGE_UPLOAD_BYTES = 25 * 1024 * 1024;

/**
 * How many uploaded files one workspace may hold. SharePoint sources do not count towards it — the cap is
 * on bytes this workspace owns, not on what it points at.
 */
export const MAX_KNOWLEDGE_UPLOAD_FILES = 20;

/**
 * Mirror of the extensions cti-sync-service's `extractor.isSupported()` accepts — **kept in sync
 * MANUALLY**, exactly like `ALLOWED_EXTENSIONS` in `useComposer.ts`. Held without the leading dot so it
 * compares against a filename's last segment. It only spares a doomed round-trip: the service checks the
 * same list and answers 415, and that answer stays the authority.
 */
export const KNOWLEDGE_UPLOAD_EXTENSIONS = new Set([
  'pdf',
  'docx',
  'xlsx',
  'pptx',
  'txt',
  'md',
  'csv',
  'json',
  'xml',
  'html',
  'htm',
  'zip',
  'py',
  'js',
  'ts',
  'java',
  'c',
  'cpp',
  'cs',
  'go',
  'rs',
  'rb',
  'php',
  'sh',
  'yaml',
  'yml',
  'log',
  'ini',
  'cfg',
]);

/**
 * `accept` value for the knowledge file picker — mirrors {@link KNOWLEDGE_UPLOAD_EXTENSIONS}. Browsers
 * filter the system dialog; drag-and-drop still relies on {@link knowledgeUploadRejection}.
 */
export const KNOWLEDGE_UPLOAD_ACCEPT = [...KNOWLEDGE_UPLOAD_EXTENSIONS]
  .map((extension) => `.${extension}`)
  .join(',');

/** Human list of what can be uploaded, used in the rejection message and in the dropzone's hint. */
export const KNOWLEDGE_UPLOAD_HINT =
  'PDF, Word (.docx), Excel (.xlsx), PowerPoint (.pptx), text, Markdown, CSV, JSON, HTML, code files and ZIP';

/**
 * Why this file cannot be uploaded, in words the user can act on — null when it can. Both rules mirror the
 * service (415 and 413); checking them here means a wrong file is refused instantly instead of after a
 * presign round-trip.
 */
export function knowledgeUploadRejection(file: {
  name: string;
  size: number;
}): string | null {
  const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!file.name.includes('.') || !KNOWLEDGE_UPLOAD_EXTENSIONS.has(extension)) {
    return `“${file.name}” is not a file type the indexer can read. Supported: ${KNOWLEDGE_UPLOAD_HINT}.`;
  }
  if (file.size > MAX_KNOWLEDGE_UPLOAD_BYTES) {
    return `“${file.name}” is larger than the 25 MB limit.`;
  }
  return null;
}

/** How many uploaded files a listed workspace already holds — the `n` of the `n/20` counter. */
export function countKnowledgeFileSources(sources: KnowledgeSource[]): number {
  return sources.filter((source) => source.type === 'files').length;
}

/**
 * The service's refusals in words that say what to do next. The presign and the register answer the same
 * codes for the same reasons, so one mapping serves both: 415 (extension outside the indexer's list), 413
 * (over the size cap), 409 (the workspace is at its upload cap), 403 (viewer), 503 (sync unreachable, so
 * nothing was registered). Anything else falls through to the service's own message.
 */
function uploadRefusalMessage(
  status: number,
  filename: string,
): string | undefined {
  if (status === 415) {
    return `“${filename}” is not a file type the indexer can read. Supported: ${KNOWLEDGE_UPLOAD_HINT}.`;
  }
  if (status === 413) return `“${filename}” is larger than the 25 MB limit.`;
  if (status === 409) {
    return `This workspace already holds ${MAX_KNOWLEDGE_UPLOAD_FILES} uploaded files. Remove one before uploading another.`;
  }
  if (status === 403)
    return 'You need editor access to upload files to this workspace.';
  if (status === 503) {
    return `“${filename}” could not be added — the sync service is unavailable, try again.`;
  }
  return undefined;
}

/** A signed slot in the knowledge bucket: PUT the bytes at `uploadUrl`, then register `s3Key`. */
export interface KnowledgeUploadTicket {
  uploadUrl: string;
  s3Key: string;
  expiresIn: number;
}

/** The snake_case presign response. `expires_in` is informational, so a missing one is not an error. */
interface KnowledgeUploadTicketWire {
  upload_url: string;
  s3_key: string;
  expires_in?: number;
}

/**
 * Ask the service for a slot in the knowledge bucket (editor+). Unlike the chat's `/v1/uploads/sign` —
 * the one camelCase body on the platform — this route is snake_case like the rest of the workspace API.
 */
export async function signKnowledgeUpload(
  workspaceId: string,
  file: { filename: string; size: number; contentType: string },
): Promise<KnowledgeUploadTicket> {
  const res = await fetch(
    `${API_BASE}/${encodeURIComponent(workspaceId)}/knowledge/uploads/sign`,
    {
      method: 'POST',
      headers: await headers(),
      body: JSON.stringify({
        filename: file.filename,
        size: file.size,
        content_type: file.contentType,
      }),
    },
  );
  if (!res.ok) {
    throw new Error(
      uploadRefusalMessage(res.status, file.filename) ??
        (await errorMessage(res, 'Failed to prepare the upload')),
    );
  }
  const wire = (await res.json()) as KnowledgeUploadTicketWire;
  return {
    uploadUrl: wire.upload_url,
    s3Key: wire.s3_key,
    expiresIn: typeof wire.expires_in === 'number' ? wire.expires_in : 0,
  };
}

/**
 * Put one local file into the workspace's knowledge: presign → PUT the bytes straight to storage →
 * register the source that makes sync index them. Three calls, one user-visible step, so a failure at any
 * of them surfaces as one message and nothing is registered for bytes that never landed.
 *
 * The PUT carries no platform bearer: the signature IS the authorisation, and a header the signature does
 * not cover makes storage refuse the request. The content type sent on the PUT is the one that was
 * signed — S3 compares them — so both come from the same variable.
 *
 * There is no byte progress. `fetch` reports none for a request body, so a caller can only show
 * uploading → uploaded/error, exactly like the chat composer does; a percentage here would be invented.
 */
export async function uploadKnowledgeFile(
  workspaceId: string,
  file: File,
): Promise<KnowledgeSource> {
  const contentType = file.type || 'application/octet-stream';
  const ticket = await signKnowledgeUpload(workspaceId, {
    filename: file.name,
    size: file.size,
    contentType,
  });
  const stored = await fetch(ticket.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!stored.ok) {
    throw new Error(
      `“${file.name}” could not be stored (${stored.status}) — check your connection and try again.`,
    );
  }
  return createKnowledgeSource(workspaceId, {
    type: 'files',
    config: {
      s3_key: ticket.s3Key,
      filename: file.name,
      size: file.size,
      content_type: contentType,
    },
    enabled: true,
  });
}

/**
 * Fetch an uploaded file's bytes (any member). The content route is bearer-authed and the service proxies
 * the object, so a raw presigned URL never reaches the browser — the caller turns this blob into an
 * object URL to hand the browser a download.
 */
export async function fetchKnowledgeSourceBlob(
  workspaceId: string,
  id: string,
): Promise<Blob> {
  const res = await fetch(
    `${sourcesPath(workspaceId)}/${encodeURIComponent(id)}/content`,
    {
      headers: await getAuthHeaders(),
    },
  );
  if (res.status === 404) {
    throw new Error('This file is no longer stored in the workspace.');
  }
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to download the file'));
  return res.blob();
}

/** Enable or disable a source (editor+). Enabling re-kicks ingest server-side. */
export async function setKnowledgeSourceEnabled(
  workspaceId: string,
  id: string,
  enabled: boolean,
): Promise<KnowledgeSource> {
  const res = await fetch(
    `${sourcesPath(workspaceId)}/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      headers: await headers(),
      body: JSON.stringify({ enabled }),
    },
  );
  if (!res.ok)
    throw new Error(
      await errorMessage(res, 'Failed to update knowledge source'),
    );
  return fromWire((await res.json()) as KnowledgeSourceWire);
}

/**
 * Delete a source (editor+). The service answers 204. A 404 means it's already gone — treated as an
 * idempotent success so a concurrent delete doesn't surface a spurious failure. A 503 means the delete
 * never reached sync: the source is still registered and still indexed, and retrying is safe, so it gets
 * a message that says exactly that instead of the generic failure.
 */
export async function deleteKnowledgeSource(
  workspaceId: string,
  id: string,
): Promise<void> {
  const res = await fetch(
    `${sourcesPath(workspaceId)}/${encodeURIComponent(id)}`,
    {
      method: 'DELETE',
      headers: await headers(),
    },
  );
  if (res.status === 503) {
    throw new Error(
      'Could not delete the source — the sync service is unavailable, try again.',
    );
  }
  if (!res.ok && res.status !== 404) {
    throw new Error(
      await errorMessage(res, 'Failed to delete knowledge source'),
    );
  }
}

/**
 * Outcome of a resync request. The service answers 202 in three different senses: `queued` (an ingest was
 * really scheduled), `disabled` (the source is paused, so the service deliberately did nothing — the
 * recovery path would have re-registered it with sync, and registration reactivates ingest, silently
 * un-pausing the source), and `unavailable` (it could not reach sync, so nothing was scheduled). The
 * caller must tell them apart — reporting either refusal as success lies to the user.
 */
export type KnowledgeResyncStatus = 'queued' | 'disabled' | 'unavailable';

/**
 * Ask the service to reindex a source from scratch (editor+). The ingest runs asynchronously, so there
 * is no updated row to parse — the caller refetches the list to watch progress. Only an explicit refusal
 * downgrades the result; a 202 whose body is missing or carries a status this build does not know still
 * means the request was accepted, so it reports `queued`.
 */
export async function resyncKnowledgeSource(
  workspaceId: string,
  id: string,
): Promise<KnowledgeResyncStatus> {
  const res = await fetch(
    `${sourcesPath(workspaceId)}/${encodeURIComponent(id)}/resync`,
    {
      method: 'POST',
      headers: await headers(),
    },
  );
  if (!res.ok)
    throw new Error(await errorMessage(res, 'Failed to start reindexing'));
  const body = (await res.json().catch(() => null)) as {
    status?: string;
  } | null;
  if (body?.status === 'unavailable') return 'unavailable';
  if (body?.status === 'disabled') return 'disabled';
  return 'queued';
}

/**
 * What a rebind actually did. Three facts, and none of them may be folded into "it worked".
 *
 * `status` is sync's own answer about the documents: `moved` transferred them onto the new connection,
 * `unchanged` means they were already there — a success, and a no-op. `documents` is how many there were.
 * `reindex` is the follow-up ingest under the new connection: `queued` means the content is being read
 * again, `skipped` is what an `unchanged` rebind gets (there was nothing to re-read), and `unavailable`
 * means the ingest could NOT be queued — the source is correctly rebound and exactly as stale as it was.
 *
 * That last one is why this shape exists at all. A rebind that fixes provenance and leaves the content
 * stale did not do the thing the person asked for, and reporting it as a plain success hides the one fact
 * they need. Both fields are raw strings for the usual reason: a newer service can answer with a value
 * this build has never seen, and an unknown answer must not be read as a confirmation.
 */
export interface KnowledgeSourceRebind {
  source: KnowledgeSource;
  status: string;
  documents: number;
  reindex: string;
}

/**
 * The two 409s this route answers with are two different problems, and only one of them has a repair the
 * person can carry out. This is the marker of that one — the target connection already holds the same
 * item as a source here, and one item cannot be indexed twice in one workspace, so the way out is to
 * delete one of the two sources.
 *
 * It matches the service's own sentence, which is a seam worth naming: the alternative was a second
 * status code or a machine-readable reason on the wire, and neither exists. When the service rewords its
 * refusal the tail below stops being added — the person still reads the service's sentence, so the
 * failure mode is a missing hint rather than a wrong one.
 */
const TARGET_ALREADY_HOLDS_IT = 'already holds this item';

/**
 * The tail added to that refusal: what to do about it. Kept out of the service's own words rather than
 * asked for from it, because it is advice about our interface — which source to remove — and the service
 * has no view on that.
 */
const DUPLICATE_SOURCE_ADVICE =
  'One item can only be a knowledge source once in a workspace, so the way out is to delete one of the two sources — this one, or the one already registered through that connection.';

/**
 * Point one knowledge source at another connection (owner/editor). This is what brings a source back to
 * life when the connection it was registered through is gone: the chunks already in the index stay where
 * they are, and re-indexing resumes under a connection somebody still holds.
 *
 * `connection_id` is the whole body, in the service's snake_case — a camelCase key is dropped by the
 * whitelist with no error, and the request would then be a rebind naming no connection.
 *
 * A refusal keeps the service's own sentence, because each one means something different and only the
 * service can tell them apart: a role that may not move a source, a connection that is not the caller's,
 * an item the target connection already holds, a source the indexer has never seen, and an indexer that
 * could not be reached at all. The one addition is {@link DUPLICATE_SOURCE_ADVICE}.
 */
export async function rebindKnowledgeSourceConnection(
  workspaceId: string,
  id: string,
  connectionId: string,
): Promise<KnowledgeSourceRebind> {
  const res = await fetch(
    `${sourcesPath(workspaceId)}/${encodeURIComponent(id)}/connection`,
    {
      method: 'PATCH',
      headers: await headers(),
      body: JSON.stringify({ connection_id: connectionId }),
    },
  );
  if (!res.ok) {
    const message = await errorMessage(
      res,
      'The source could not be bound to that connection',
    );
    throw new Error(
      res.status === 409 && message.includes(TARGET_ALREADY_HOLDS_IT)
        ? `${message}. ${DUPLICATE_SOURCE_ADVICE}`
        : message,
    );
  }
  const wire = (await res.json()) as KnowledgeSourceWire & {
    rebind?: { status?: string; documents?: number; reindex?: string };
  };
  return {
    source: fromWire(wire),
    status: wire.rebind?.status ?? '',
    documents:
      typeof wire.rebind?.documents === 'number' ? wire.rebind.documents : 0,
    reindex: wire.rebind?.reindex ?? '',
  };
}

/** Query-key factory so mutations can invalidate precisely. */
export const knowledgeSourceKeys = {
  list: (workspaceId: string) => ['workspace-knowledge', workspaceId] as const,
};

/**
 * How often the list refreshes while something is still being indexed. Ingest progress moves on the scale
 * of tens of seconds, and every read of this list costs the service a batched call into sync, so a
 * tighter interval would buy nothing and charge for it. The poll also stops on its own (see
 * {@link isIndexingPassInFlight}), and TanStack's `refetchIntervalInBackground` stays at its default
 * `false`, so a tab left open in the background is not polling at all.
 */
const INDEXING_POLL_MS = 15_000;

/**
 * Whether sync still has a pass on for this source. The server owns the answer — `queued` covers the job
 * it has accepted but not started, which no counter can express — so the client reads the field instead
 * of deriving it. A status-less source (`indexing: null`) is never polled: the service could not reach
 * sync, and refetching would only ask the same broken question again.
 *
 * Anything other than `settled` counts as in flight, including a state this build does not know: `pass`
 * is an open union, and treating an unfamiliar value as finished would silently freeze the badges.
 */
function isIndexingPassInFlight(source: KnowledgeSource): boolean {
  return source.indexing !== null && source.indexing.pass !== 'settled';
}

/**
 * TanStack Query factory for a workspace's knowledge-source list. It polls itself while sync reports an
 * unsettled pass on any source and goes quiet once they have all settled — the progress badges are the
 * point of this list, and nothing else refreshes it (`refetchOnWindowFocus` is off app-wide), so without
 * this the counts froze at whatever the first load saw.
 */
export function knowledgeSourcesQueryOptions(workspaceId: string) {
  return queryOptions({
    queryKey: knowledgeSourceKeys.list(workspaceId),
    queryFn: () => listKnowledgeSources(workspaceId),
    staleTime: 30_000,
    refetchInterval: (query) =>
      (query.state.data ?? []).some(isIndexingPassInFlight)
        ? INDEXING_POLL_MS
        : false,
  });
}

/** Create a source, then invalidate the list. */
export function useCreateKnowledgeSource(
  workspaceId: string,
): UseMutationResult<KnowledgeSource, Error, CreateKnowledgeSourceInput> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateKnowledgeSourceInput) =>
      createKnowledgeSource(workspaceId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeSourceKeys.list(workspaceId),
      });
    },
  });
}

/** Toggle a source's enabled flag, then invalidate the list. */
export function useSetKnowledgeSourceEnabled(
  workspaceId: string,
): UseMutationResult<KnowledgeSource, Error, { id: string; enabled: boolean }> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      setKnowledgeSourceEnabled(workspaceId, id, enabled),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeSourceKeys.list(workspaceId),
      });
    },
  });
}

/**
 * Kick a reindex of a source, then invalidate the list so the new progress is picked up. The mutation
 * resolves with the resync status, so the caller can tell "queued" from "nothing was queued".
 */
export function useResyncKnowledgeSource(
  workspaceId: string,
): UseMutationResult<KnowledgeResyncStatus, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => resyncKnowledgeSource(workspaceId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeSourceKeys.list(workspaceId),
      });
    },
  });
}

/**
 * Rebind a source onto one of the caller's own connections, then invalidate the list so the row shows the
 * state it is actually in. The mutation resolves with the whole {@link KnowledgeSourceRebind}, because the
 * caller has three different things to say depending on what came back.
 */
export function useRebindKnowledgeSourceConnection(
  workspaceId: string,
): UseMutationResult<
  KnowledgeSourceRebind,
  Error,
  { id: string; connectionId: string }
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, connectionId }: { id: string; connectionId: string }) =>
      rebindKnowledgeSourceConnection(workspaceId, id, connectionId),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeSourceKeys.list(workspaceId),
      });
    },
  });
}

/**
 * Delete a source, then invalidate the list. Deliberately NOT optimistic: a delete can fail with a 503
 * that leaves the source in place, and dropping the row first would leave the list lying about what the
 * workspace still indexes. The row disappears only once the service has confirmed the delete.
 */
export function useDeleteKnowledgeSource(
  workspaceId: string,
): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteKnowledgeSource(workspaceId, id),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: knowledgeSourceKeys.list(workspaceId),
      });
    },
  });
}
